import { useState, useEffect } from 'react';
import { FileText, TrendingUp, PieChart, BarChart3, Lock, Download, Loader2, FileDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ExportDialog } from '@/components/ExportDialog';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';
import { DateRangeSelector, getDefaultDateRange, DateRange } from '@/components/Reports/DateRangeSelector';
import { VendorSpendingChart } from '@/components/Reports/VendorSpendingChart';
import { ReportsSkeleton } from '@/components/Reports/ReportsSkeleton';
import { SEO } from '@/components/SEO';
import { generateReconciliationReportPDF } from '@/lib/pdfExport';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';

const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canAccess, nextTier, nextTierPrice, tier } = usePlanLimits();
  const hasCustomReports = canAccess('hasCustomReports');
  
  const [isLoading, setIsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [stats, setStats] = useState({
    totalMatched: 0,
    totalUnmatched: 0,
    blockedInvoices: 0,
    riskySavings: 0
  });
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [vendorData, setVendorData] = useState<any[]>([]);
  const [runwayData, setRunwayData] = useState<any[]>([]);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchReportData();
  }, [user, dateRange]);

  const fetchReportData = async () => {
    if (!user) return;
    setIsLoading(true);

    const fromDate = dateRange.from.toISOString().split('T')[0];
    const toDate = dateRange.to.toISOString().split('T')[0];

    const [bankRes, invoiceRes, matchHistoryRes] = await Promise.all([
      supabase.from('bank_transactions')
        .select('status, created_at, amount')
        .eq('user_id', user.id)
        .gte('date', fromDate)
        .lte('date', toDate),
      supabase.from('invoices')
        .select('status, risk_score, amount, vendor, created_at')
        .eq('user_id', user.id)
        .gte('date', fromDate)
        .lte('date', toDate),
      supabase.from('match_history')
        .select('matched_at')
        .eq('user_id', user.id)
        .gte('matched_at', fromDate)
        .lte('matched_at', toDate)
    ]);

    const matched = bankRes.data?.filter(b => b.status === 'matched').length || 0;
    const unmatched = bankRes.data?.filter(b => b.status === 'unmatched').length || 0;
    const blocked = invoiceRes.data?.filter(i => i.status === 'blocked').length || 0;
    const savings = invoiceRes.data
      ?.filter(i => i.status === 'blocked')
      .reduce((sum, i) => sum + i.amount, 0) || 0;

    const totalData = (bankRes.data?.length || 0) + (invoiceRes.data?.length || 0);
    setHasData(totalData > 0);

    setStats({
      totalMatched: matched,
      totalUnmatched: unmatched,
      blockedInvoices: blocked,
      riskySavings: savings
    });

    // Status distribution for pie chart
    setStatusData([
      { name: 'Matched', value: matched },
      { name: 'Unmatched', value: unmatched },
      { name: 'Blocked', value: blocked }
    ].filter(d => d.value > 0));

    // Aggregate monthly data
    const monthlyAggregated: Record<string, { matched: number; unmatched: number; savings: number }> = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Initialize last 6 months
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = months[d.getMonth()];
      monthlyAggregated[key] = { matched: 0, unmatched: 0, savings: 0 };
    }

    // Count matches by month
    matchHistoryRes.data?.forEach(match => {
      const date = new Date(match.matched_at);
      const monthKey = months[date.getMonth()];
      if (monthlyAggregated[monthKey]) {
        monthlyAggregated[monthKey].matched++;
      }
    });

    // Count unmatched and savings by month
    bankRes.data?.filter(t => t.status === 'unmatched').forEach(txn => {
      const date = new Date(txn.created_at);
      const monthKey = months[date.getMonth()];
      if (monthlyAggregated[monthKey]) {
        monthlyAggregated[monthKey].unmatched++;
      }
    });

    invoiceRes.data?.filter(i => i.status === 'blocked').forEach(inv => {
      const date = new Date(inv.created_at);
      const monthKey = months[date.getMonth()];
      if (monthlyAggregated[monthKey]) {
        monthlyAggregated[monthKey].savings += inv.amount;
      }
    });

    const monthlyDataArray = Object.entries(monthlyAggregated).map(([month, data]) => ({
      month,
      matched: data.matched,
      unmatched: data.unmatched,
      savings: data.savings,
    }));

    setMonthlyData(monthlyDataArray);

    // Vendor spending aggregation
    const vendorAgg: Record<string, { amount: number; count: number }> = {};
    invoiceRes.data?.forEach(inv => {
      if (!vendorAgg[inv.vendor]) {
        vendorAgg[inv.vendor] = { amount: 0, count: 0 };
      }
      vendorAgg[inv.vendor].amount += inv.amount;
      vendorAgg[inv.vendor].count++;
    });

    const vendorDataArray = Object.entries(vendorAgg).map(([vendor, data]) => ({
      vendor,
      amount: data.amount,
      count: data.count,
    }));

    setVendorData(vendorDataArray);

    // Runway projection data (cumulative cash flow)
    let runningTotal = 0;
    const runwayDataArray = monthlyDataArray.map((m, idx) => {
      const cashFlow = (bankRes.data || [])
        .filter(t => {
          const date = new Date(t.created_at);
          return months[date.getMonth()] === m.month;
        })
        .reduce((sum, t) => sum + t.amount, 0);
      runningTotal += cashFlow;
      return {
        month: m.month,
        cashFlow,
        cumulative: runningTotal,
      };
    });

    setRunwayData(runwayDataArray);
    setIsLoading(false);
  };

  const handleExportPDF = () => {
    setExporting(true);
    try {
      generateReconciliationReportPDF({
        dateRange,
        stats,
        monthlyData,
        vendorData,
        runwayData,
      });
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 h-full overflow-y-auto">
        <SEO title="Analytics & Reports - Sentinel" description="Financial analytics and reporting" noindex />
        <ReportsSkeleton />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 h-full overflow-y-auto">
      <SEO title="Analytics & Reports - Sentinel" description="Financial analytics and reporting" noindex />
      
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
            <FileText className="text-primary" />
            Analytics & Reports
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {hasData 
              ? 'Comprehensive insights into your financial operations'
              : 'Import transactions and invoices to see analytics'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
          {hasCustomReports && hasData && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportPDF}
                disabled={exporting}
                className="rounded-2xl"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4 mr-2" />
                )}
                Export PDF
              </Button>
              <ExportDialog />
            </>
          )}
        </div>
      </div>

      {/* Feature Gate for Free tier */}
      {!hasCustomReports && (
        <Card className="border-primary/50 bg-primary/5 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Lock className="h-12 w-12 text-primary shrink-0" />
              <div className="text-center sm:text-left flex-1">
                <h3 className="text-lg font-bold text-foreground mb-1">Custom Reports - {tier === 'free' ? 'Starter' : 'Pro'} Feature</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Upgrade to access detailed analytics, custom reports, and CSV/PDF exports.
                </p>
                <Button onClick={() => navigate('/pricing')}>
                  Upgrade to {nextTier ? nextTier.charAt(0).toUpperCase() + nextTier.slice(1) : 'Starter'} - {nextTierPrice}/month
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
        <Card className="p-4 md:p-6 border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Matched</p>
              <h3 className="text-xl md:text-2xl font-bold text-success">{stats.totalMatched}</h3>
            </div>
            <TrendingUp className="text-success hidden sm:block" size={32} />
          </div>
        </Card>
        <Card className="p-4 md:p-6 border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Unmatched</p>
              <h3 className="text-xl md:text-2xl font-bold text-warning">{stats.totalUnmatched}</h3>
            </div>
            <BarChart3 className="text-warning hidden sm:block" size={32} />
          </div>
        </Card>
        <Card className="p-4 md:p-6 border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Blocked</p>
              <h3 className="text-xl md:text-2xl font-bold text-destructive">{stats.blockedInvoices}</h3>
            </div>
            <PieChart className="text-destructive hidden sm:block" size={32} />
          </div>
        </Card>
        <Card className="p-4 md:p-6 border-border/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-muted-foreground">Savings</p>
              <h3 className="text-xl md:text-2xl font-bold text-primary">{formatCurrency(stats.riskySavings)}</h3>
            </div>
            <FileText className="text-primary hidden sm:block" size={32} />
          </div>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Monthly Trends */}
        <Card className="p-4 md:p-6 border-border/50">
          <h3 className="text-base md:text-lg font-bold text-foreground mb-4">Monthly Reconciliation Trends</h3>
          <div className="h-[250px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="matched" fill="#06b6d4" name="Matched" radius={[4, 4, 0, 0]} />
                <Bar dataKey="unmatched" fill="#f59e0b" name="Unmatched" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Status Distribution */}
        <Card className="p-4 md:p-6 border-border/50">
          <h3 className="text-base md:text-lg font-bold text-foreground mb-4">Status Distribution</h3>
          <div className="h-[250px] md:h-[300px]">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data for selected period
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        {/* Vendor Spending */}
        <VendorSpendingChart data={vendorData} />

        {/* Runway Projection */}
        <Card className="p-4 md:p-6 border-border/50">
          <h3 className="text-base md:text-lg font-bold text-foreground mb-4">Cumulative Cash Flow</h3>
          <div className="h-[250px] md:h-[300px]">
            {runwayData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={runwayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value: number) => [formatCurrency(value), '']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="cumulative" 
                    stroke="#06b6d4" 
                    fill="#06b6d4" 
                    fillOpacity={0.2}
                    name="Cumulative"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cashFlow" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Monthly Flow"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data for selected period
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Monthly Savings Trend */}
      {hasCustomReports && (
        <Card className="p-4 md:p-6 border-border/50">
          <h3 className="text-base md:text-lg font-bold text-foreground mb-4">Monthly Savings from Blocked Invoices</h3>
          <div className="h-[200px] md:h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value: number) => [formatCurrency(value), 'Savings']}
                />
                <Bar dataKey="savings" fill="#10b981" name="Savings" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
